import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, output, Signal, viewChildren } from '@angular/core';
import { findDashboardOption, optionValuesForRol } from '@cumlaude/shared-dashboard-registry';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { WeergaveOptieComponent } from '../../../shared/components/weergave-optie/weergave-optie.component';
import { AanwezigheidUitsplitsing } from '../../../services/weergave-opties';
import { Interventielaag } from '@cumlaude/metadata';
import { InterventielaagListPanelComponent } from '../interventielaag-list-panel/interventielaag-list-panel.component';
import { interventielaagDetails, JaarMaand } from '../../../services/schoolaanwezigheid';
import {
	BasicFilterExpression,
	DataService,
	ExportFilter,
	extractDynamicSelectionExpressions,
	FilterExpression,
} from '../../../services/data.service';
import { QueryParamStateService } from '../../../services/query-param-state.service';
import {
	addMonths,
	getEindjaar,
	getMaandLabelKort,
	getSchooljaarForDate,
	getSchooljaarTovHuidig,
	isHuidigeMaand,
	today,
} from '@cumlaude/shared-utils';
import { get, isUndefined, last, range, sortBy } from 'lodash-es';
import { FilterService } from '../../../services/filter.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DataRequestContext } from '../../../shared/dashboard/base-dashboard/dashboard-context';
import { createSelection, SelectionConfig } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { AD_HOC_SELECTION_ID } from '../../../services/leerling-selectie.service';
import { att } from '../../../services/measures';
import { SchoolaanwezigheidA, SchoolaanwezigheidRequestContext } from '../aanwezigheid-overzicht.component';
import { ExportablePanel, TableExportPage } from '../../../services/exportable';
import { ExportOptions } from '../../../services/export.service';
import { ExportColumnDef } from '../../../shared/dashboard/data-tree-table/data-tree-table';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserService } from '../../../services/user.service';
import { Level } from '../../../services/data-tree';

@Component({
	selector: 'app-overzicht-interventielagen',
	templateUrl: './overzicht-interventielagen.component.html',
	styleUrls: ['./overzicht-interventielagen.component.scss'],
	imports: [IconComponent, TooltipDirective, WeergaveOptieComponent, InterventielaagListPanelComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverzichtInterventielagenComponent implements ExportablePanel, OnDestroy {
	private readonly qp = inject(QueryParamStateService);
	private readonly filterService = inject(FilterService);
	private readonly dataService = inject(DataService);
	private readonly userService = inject(UserService);

	requestContext = input.required<SchoolaanwezigheidRequestContext>();

	/** Interventielagen in volgorde rood-geel-groen */
	interventielagen = Object.values(Interventielaag).reverse();
	schooljaar: Signal<string | undefined> = toSignal(this.filterService.observeAsInput('aw_nm_schooljaar'));

	pageFilter = computed(() => {
		if (this.aanwezigheiduitsplitsing() === AanwezigheidUitsplitsing.LEERLING) {
			// We verwijderen de leerlingselecties uit het pageFilter. Leerlingselecties zitten ook al in het laagFilter, en hebben dus in de hoofdquery
			// geen toegevoegde waarde meer, terwijl ze wel slecht zijn voor de performance.
			const [_dynamicFilters, alteredFilter] = extractDynamicSelectionExpressions(this.requestContext().filter);
			return alteredFilter;
		}
		return this.requestContext().filter;
	});

	interventielaagListPanels = viewChildren(InterventielaagListPanelComponent);

	laagFilter: Record<Interventielaag, Signal<FilterExpression | undefined>>;

	totals: Record<Interventielaag, Subject<number | undefined>>;

	totalChanged = output<number | undefined>();

	exportMode = input<boolean>(false);

	constructor() {
		this.laagFilter = {} as Record<Interventielaag, Signal<FilterExpression | undefined>>;
		this.totals = {} as Record<Interventielaag, Subject<number | undefined>>;
		for (const laag of this.interventielagen) {
			this.laagFilter[laag] = computed(() => {
				const context = this.requestContext();
				const selection = this.createInterventielaagSelection(context, laag);
				return selection?.expression;
			});
			this.totals[laag] = new Subject<number | undefined>();
		}
		combineLatest(Object.values(this.totals))
			.pipe(
				takeUntilDestroyed(),
				map((totals) => {
					// Geef undefined terug tot alle totalen geladen zijn
					if (totals.some((total) => isUndefined(total))) return undefined;
					return totals.reduce((acc, curr) => acc! + curr!, 0);
				})
			)
			.subscribe((total) => {
				this.totalChanged.emit(total);
			});
	}

	ngOnDestroy() {
		for (const laag of this.interventielagen) {
			this.totals[laag].complete();
		}
	}

	aanwezigheiduitsplitsing = this.qp.signal_aanwezigheiduitsplitsing();

	laatsteMaanden: Signal<JaarMaand[]> = computed(() => {
		const schooljaar = this.schooljaar();
		if (!schooljaar) return [];

		const huidigSchooljaar = getSchooljaarTovHuidig(0);
		if (schooljaar < huidigSchooljaar) {
			const eindjaar = getEindjaar(schooljaar);
			return range(5, 8).map((maand) => ({ nr_jaar: `${eindjaar}`, nr_maand: `${maand}`, voorlopig: false }));
		} else {
			const eersteDagVdMaand = today();
			eersteDagVdMaand.setDate(1); // anders wordt addMonths(31 okt, -1) -> 1 okt.
			return range(-3, 1).flatMap((x) => {
				const d = addMonths(eersteDagVdMaand, x);
				return getSchooljaarForDate(d) === schooljaar
					? [{ nr_jaar: String(d.getFullYear()), nr_maand: String(d.getMonth() + 1), voorlopig: x === 0 }]
					: [];
			});
		}
	});

	/**
	 * De maand waarop gesorteerd wordt in de drie lijstjes is de laatste maand, behalve als dit de huidige maand is en er nog een eerdere maand is (dan nemen we die, want die is volledig).
	 */
	sortMonth = computed(() => {
		const laatsteMaanden = this.laatsteMaanden();
		return last(laatsteMaanden.filter((jm) => !jm.voorlopig)) ?? last(laatsteMaanden);
	});

	private readonly uitsplitsingOption = findDashboardOption('aanwezigheiduitsplitsing');

	protected uitsplitsingFilter = (uitsplitsing: AanwezigheidUitsplitsing) =>
		!this.uitsplitsingOption || optionValuesForRol(this.uitsplitsingOption, this.userService.activeRol()).includes(uitsplitsing);

	private getSelectionConfig(_context: DataRequestContext<SchoolaanwezigheidA>): SelectionConfig<SchoolaanwezigheidA> {
		const x_leerlingselectie = JSON.stringify([{ id: AD_HOC_SELECTION_ID, naam: '' }]);
		return {
			detailUrl: `/aanwezigheid/statistieken?x_leerlingselectie=${x_leerlingselectie}`,
			mergeQueryParams: true,
			provider: 'schoolaanwezigheid',
			getSize: att('aw_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	/**
	 * De uitsplitsing wordt gedaan o.b.v. de laatste maand waarvoor er data aanwezig is en die niet de huidige maand is. Als er behalve de huidige maand
	 * geen data aanwezig is, gebruik dan de huidige maand.
	 */
	private createInterventielaagSelection(context: SchoolaanwezigheidRequestContext, laag: Interventielaag) {
		if (!context.dataRoot) return undefined;

		// de backend levert de maanden tegenwoordig op jan-dec volgorde uit
		const monthLevels = this.opSchooljaarVolgorde(context.dataRoot.c);
		const lvl = last(monthLevels.filter((monthLvl) => !this.isVoorlopig(monthLvl))) ?? last(monthLevels);
		if (!lvl) return undefined;

		const config = {
			...this.getSelectionConfig(context),
			extraFilters: [new BasicFilterExpression(['aw_fun_nm_interventielaag'], laag)],
			moveNestedSelectionsUp: false,
		};
		return createSelection(context, lvl.r[0], config);
	}

	private isVoorlopig(monthLvl: Level<SchoolaanwezigheidA, number[]>): boolean {
		return isHuidigeMaand(this.schooljaar(), monthLvl.k!);
	}

	private opSchooljaarVolgorde(monthLevels: Level<SchoolaanwezigheidA, number[]>[]): Level<SchoolaanwezigheidA, number[]>[] {
		return sortBy(monthLevels, (lvl) => (Number(lvl.k) + 4) % 12);
	}

	async exportAsTable(exportOptions: ExportOptions, filters: ExportFilter[]): Promise<TableExportPage> {
		const maanden: ExportColumnDef[] = this.laatsteMaanden()
			.map((jaarMaand) => getMaandLabelKort(jaarMaand.nr_maand) + (jaarMaand.voorlopig ? ' (voorl)' : ''))
			.map((maand) => ({ name: `% ${maand}`, type: 'percentage', format: '1.0-0' }));
		let uitsplitsingColumns: ExportColumnDef[];
		switch (this.aanwezigheiduitsplitsing()) {
			case AanwezigheidUitsplitsing.LEERLING:
				uitsplitsingColumns = [
					{ name: 'Leerlingnaam', type: 'string' },
					{ name: 'Leerlingnummer', type: 'number' },
				];
				break;
			case AanwezigheidUitsplitsing.KLAS:
				uitsplitsingColumns = [{ name: 'Klas', type: 'string' }];
				break;
			case AanwezigheidUitsplitsing.VAK:
				uitsplitsingColumns = [{ name: 'Vak', type: 'string' }];
				break;
		}
		const columns: ExportColumnDef[] = [...uitsplitsingColumns, { name: 'Interventielaag', type: 'string' }, ...maanden];
		const goedeAanwezigheid = await this.getElementen(Interventielaag.GOEDE_AANWEZIGHEID);
		const beginnendVerzuim = await this.getElementen(Interventielaag.BEGINNEND_VERZUIM);
		const aanhoudendVerzuim = await this.getElementen(Interventielaag.AANHOUDEND_VERZUIM);

		return {
			name: 'Overzicht',
			...(exportOptions.showTitle
				? { titles: ['Overzicht', `% aanwezigheid laatste maand, ${this.aanwezigheiduitsplitsing().toLowerCase()}`] }
				: {}),
			...(exportOptions.showFilters ? { filters } : {}),
			data: [
				{
					columns,
					rows: [...goedeAanwezigheid, ...beginnendVerzuim, ...aanhoudendVerzuim],
				},
			],
		};
	}

	private async getElementen(interventielaag: Interventielaag) {
		const interventielaagPanel = this.interventielaagListPanels().find((panel) => panel.laag() == interventielaag)!;
		let { f, laagFilter, g, off } = interventielaagPanel.requestParams();

		return firstValueFrom(
			this.dataService
				.getSchoolAanwezigheidDetails({
					f,
					laagFilter,
					g,
					month: interventielaagPanel.maanden(),
					off,
					sortmonth: this.sortMonth(),
					sortdir: 'desc',
				})
				.pipe(
					map((response) => response.rows),
					map((rows) =>
						rows.map((row) => {
							let rowValues = [];
							switch (this.aanwezigheiduitsplitsing()) {
								case AanwezigheidUitsplitsing.LEERLING:
									rowValues.push({ value: row.ll_fun_volledige_naam }, { value: row.aw_nr_leerling });
									break;
								case AanwezigheidUitsplitsing.KLAS:
									rowValues.push({ value: row.aw_nm_klas });
									break;
								case AanwezigheidUitsplitsing.VAK:
									rowValues.push({ value: row.aw_fun_nm_vak });
									break;
							}
							rowValues.push({ value: interventielaag });

							this.laatsteMaanden()
								.map((jaarMaand) => jaarMaand.nr_maand)
								.map((maand) => get(row, `aw_m_perc_aanwezig-${maand}`))
								.map((value) => ({ value }))
								.forEach((value) => rowValues.push(value));

							return rowValues;
						})
					)
				)
		);
	}

	protected readonly AanwezigheidUitsplitsing = AanwezigheidUitsplitsing;
	protected readonly interventielaagDetails = interventielaagDetails;
}
